import {
  PAYMENT_MEMBERS,
  readMoneyV2,
  readPaymentMoneyV2,
  readSignedMoneyV2,
  type MoneyV2,
  type PaymentMoneyV2,
  type ProjectionErrorCode,
  type SignedMoneyV2,
} from "@voltie/localization-web";
import type {
  BilledAmountBasis,
  DurationBreakdown,
  DurationIssue,
  OrgSessionsReportDriver,
  OrgSessionsReportRange,
  PaymentState,
  ProcessingFeeBasis,
  RevenueSource,
} from "@/lib/api";
import { readReportTotalsMoney, type OrgReportTotalsMoney } from "@/lib/orgReportTotals";
import type { PaymentFacts } from "@/lib/unitSessions";

// GET /api/organizations/<id>/sessions/report with money contract 2 (route family F8 S2a, S2b):
// the report rows, totals and period totals plus "money_contract": 2 and the answer's scope.
// An admin answer ("full") carries each row's payment record as rows[i].money_v2
// (payment_money_v2) and its recorded fees as rows[i].fees_money_v2 (report_fees_money_v2),
// and each totals object its money_v2 and summary_money_v2 (orgReportTotals.ts); those are
// the only money read. A manager answer ("session_only") carries no money and is never
// classified. Every legacy row and totals money key is dropped unread, also for USD: a
// served USD row with an unknown total still has legacy 0.0 amounts, and a legacy-formula
// USD row still has formula fees. Nothing is summed, converted, derived or defaulted. A 200
// without the marker is invalid as a whole report; a row or totals object whose money
// cannot be read keeps its other fields.

/** Legacy money keys of a report row: payment amounts, cents and aliases, fees, payout, owes, net and the fee policies (server REPORT_ROW_LEGACY_MONEY_KEYS). */
const REPORT_ROW_LEGACY_MONEY_KEYS = [
  "billed_amount",
  "captured_payment",
  "amount_due",
  "authorized_hold_amount",
  "outstanding_amount",
  "estimated_stripe_fee",
  "voltie_fee",
  "payout_to_organization",
  "organization_owes_voltie",
  "organization_net",
  "captured_amount_cents",
  "voltie_fee_cents",
  "estimated_stripe_fee_cents",
  "organization_net_cents",
  "payout_to_organization_cents",
  "organization_owes_voltie_cents",
  "voltie_fee_policy",
  "stripe_fee_policy",
  "processing_fee_policy",
  "outstanding_cents",
  "amount_due_cents",
  "captured_amount",
] as const;

/** Legacy money keys of a totals object (server REPORT_TOTALS_LEGACY_MONEY_KEYS). */
const REPORT_TOTALS_LEGACY_MONEY_KEYS = [
  "total_billed_amount",
  "total_captured_payment",
  "total_estimated_stripe_fee",
  "total_voltie_fee",
  "total_payout_to_organization",
  "total_organization_owes_voltie",
  "total_organization_net",
  "total_billed_estimated_amount",
  "total_billed_estimated_cents",
  "total_known_outstanding_amount",
  "total_known_outstanding_cents",
  "attention_known_outstanding_amount",
  "attention_known_outstanding_cents",
  "total_billed_amount_cents",
  "total_voltie_fee_cents",
  "total_estimated_stripe_fee_cents",
  "total_payout_to_organization_cents",
  "total_organization_owes_voltie_cents",
  "total_organization_net_cents",
] as const;

export type OrgReportScope = "full" | "session_only";

export type OrgSessionsReportErrorCode =
  | ProjectionErrorCode
  | "money_contract"
  | "outstanding_known"
  | "recorded_only"
  | "net_split"
  | "currency_order"
  | "member_counts"
  | "member_complete"
  | "member_amount"
  | "population_identity"
  | "group_without_entry"
  | "group_rows";

export interface InvalidRead {
  readonly status: "invalid";
  readonly error: OrgSessionsReportErrorCode;
  readonly path: string;
  readonly detail: string;
}

export const REPORT_FEE_MEMBERS = ["processing_fee", "voltie_fee", "payout_to_organization", "organization_owes_voltie", "organization_net"] as const;

type ReportFeeMember = (typeof REPORT_FEE_MEMBERS)[number];

/** A row's recorded fees (report_fees_money_v2): known only from the invoice's recorded revenue block, null = unknown. */
export interface ReportFeesMoneyV2 {
  readonly processing_fee: MoneyV2 | null;
  readonly voltie_fee: MoneyV2 | null;
  readonly payout_to_organization: MoneyV2 | null;
  readonly organization_owes_voltie: MoneyV2 | null;
  /** Validated, never displayed (the legacy report did not display it either). */
  readonly organization_net: SignedMoneyV2 | null;
}

/** The money of one row: its payment and fee blocks (admin), none (manager), or invalid for this row only. */
export type OrgReportRowMoney =
  | { readonly status: "value"; readonly payment: PaymentMoneyV2; readonly fees: ReportFeesMoneyV2 }
  | { readonly status: "none" }
  | InvalidRead;

type FullRowMoney = Exclude<OrgReportRowMoney, { readonly status: "none" }>;

/** The fields of every report row, without money_v2, fees_money_v2 and the legacy money keys. */
interface OrgReportRowFacts {
  readonly start_time: string;
  // "YYYY-MM-DD HH:MM:SS" (UTC), null for the rare row without one. duration_seconds is
  // plugged-in time; charging / idle / suspended are null unless exact (duration_breakdown
  // and duration_issues say why).
  readonly end_time: string | null;
  readonly duration_seconds: number | null;
  readonly charging_seconds: number | null;
  readonly idle_seconds: number | null;
  readonly suspended_seconds?: number | null;
  readonly duration_breakdown?: DurationBreakdown | null;
  readonly duration_issues?: readonly DurationIssue[];
  readonly unit_id: string;
  readonly customer_user_id?: number | null;
  readonly customer_name: string | null;
  readonly energy_kwh: number;
}

/** An admin row: the payment facts, the report's admin facts and its money, read or invalid for this row only. */
export interface OrgReportFullRow extends OrgReportRowFacts, Readonly<PaymentFacts> {
  readonly revenue_source?: RevenueSource;
  readonly revenue_available?: boolean;
  readonly billing_source?: string | null;
  readonly billed_amount_basis?: BilledAmountBasis;
  readonly processing_fee_basis?: ProcessingFeeBasis;
  readonly billing_finalized_at?: string | null;
  readonly billing_finalization_started_at?: string | null;
  readonly money: FullRowMoney;
}

/** A manager row: the allow-listed session fields only, never classified. */
export interface OrgReportSessionRow extends OrgReportRowFacts {
  readonly money: { readonly status: "none" };
}

export type OrgReportRow = OrgReportFullRow | OrgReportSessionRow;

/** A totals object without its raw money siblings and legacy money keys: counters and the typed money. The admin counters are absent on a manager answer. */
export interface OrgReportTotals {
  /** Read from this object's own siblings (admin), none (manager), or invalid for this object only. */
  readonly money: OrgReportTotalsMoney;
  readonly total_sessions: number;
  readonly total_energy_kwh: number;
  // Admin only.
  readonly frozen_count?: number;
  readonly legacy_fallback_count?: number;
  readonly uncollected_count?: number;
  readonly free_charging_schedule_count?: number;
  readonly revenue_source_counts?: Readonly<Partial<Record<RevenueSource, number>>>;
  readonly revenue_unavailable_count?: number;
  readonly billed_unavailable_count?: number;
  readonly unavailable_billed_count?: number;
  readonly billed_invoice_count?: number;
  readonly billed_estimated_count?: number;
  readonly processing_fee_basis_counts?: Readonly<Partial<Record<ProcessingFeeBasis, number>>>;
  readonly payment_attention_count?: number;
  readonly payment_action_required_count?: number;
  readonly engineering_investigation_count?: number;
  readonly manual_review_required_count?: number;
  readonly known_outstanding_session_count?: number;
  readonly attention_known_outstanding_session_count?: number;
  readonly unknown_outstanding_session_count?: number;
  readonly payment_state_counts?: Readonly<Partial<Record<PaymentState, number>>>;
}

export interface OrgReport {
  readonly scope: OrgReportScope;
  readonly organization: { readonly id: number; readonly name: string | null };
  readonly rows: readonly OrgReportRow[];
  /** Counters of `rows`, the selection shown after the attention filter. */
  readonly totals: OrgReportTotals;
  /** Counters of every session the range, unit and driver filters matched. */
  readonly period_totals: OrgReportTotals;
  readonly count: number;
  readonly range?: Readonly<OrgSessionsReportRange>;
  readonly filters?: { readonly unit_id: string | null; readonly user_id: number | null; readonly attention: boolean };
  readonly drivers?: readonly Readonly<OrgSessionsReportDriver>[];
}

export type OrgSessionsReportRead = { readonly status: "value"; readonly value: OrgReport } | InvalidRead;

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));
const invalid = (error: OrgSessionsReportErrorCode, path: string, detail: string): InvalidRead => ({ status: "invalid", error, path, detail });
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

function deepFreeze<T>(value: T): T {
  if (typeof value === "object" && value !== null) {
    for (const entry of Object.values(value)) deepFreeze(entry);
    Object.freeze(value);
  }
  return value;
}

/** A frozen deep copy of `record` without `keys`; the dropped values are never read. */
function frozenWithout(record: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const copy: Record<string, unknown> = {};
  for (const key of Object.keys(record)) if (!keys.includes(key)) copy[key] = structuredClone(record[key]);
  return deepFreeze(copy);
}

const frozenCopy = <T>(value: T): T => deepFreeze(structuredClone(value));

type FeeAmount = MoneyV2 | SignedMoneyV2;

// The fees agree in currency and exponent with the row's payment block, or with the first
// known fee when every payment member is unknown. recorded_only and net_split are the frozen
// definition's own rules; a row breaking them is not shown rather than shown wrong. The
// legacy cents beside the block are never compared (MV-7 is the server's check).
function readRowFees(row: Record<string, unknown>, path: string, payment: PaymentMoneyV2): { readonly status: "value"; readonly value: ReportFeesMoneyV2 } | InvalidRead {
  const at = `${path}.fees_money_v2`;
  const block = row.fees_money_v2;
  if (block === undefined || block === null) return invalid("missing_field", at, "fees_money_v2");
  const prototype = isRecord(block) ? Object.getPrototypeOf(block) : undefined;
  if (prototype !== Object.prototype && prototype !== null) return invalid("not_object", at, show(block));
  const record = block as Record<string, unknown>;
  const missing = REPORT_FEE_MEMBERS.find((key) => !Object.hasOwn(record, key));
  if (missing !== undefined) return invalid("missing_field", `${at}.${missing}`, missing);
  const extra = Object.keys(record).find((key) => !REPORT_FEE_MEMBERS.includes(key as ReportFeeMember));
  if (extra !== undefined) return invalid("forbidden_key", `${at}.${extra}`, "not a report_fees_money_v2 member");

  let head: FeeAmount | null = PAYMENT_MEMBERS.map((key) => payment[key]).find((amount) => amount !== null) ?? null;
  const fees: Partial<Record<ReportFeeMember, FeeAmount | null>> = {};
  for (const key of REPORT_FEE_MEMBERS) {
    const read = key === "organization_net" ? readSignedMoneyV2(record[key]) : readMoneyV2(record[key]);
    if (read.status === "invalid") return invalid(read.error, `${at}.${key}`, read.detail);
    const amount = read.status === "value" ? read.value : null;
    if (amount !== null) {
      head ??= amount;
      if (amount.currency !== head.currency) return invalid("currency_mismatch", `${at}.${key}.currency`, `${amount.currency} in a ${head.currency} row`);
      if (amount.exponent !== head.exponent) return invalid("exponent_mismatch", `${at}.${key}.exponent`, `${amount.exponent} in a row with exponent ${head.exponent}`);
    }
    fees[key] = amount;
  }

  const known = REPORT_FEE_MEMBERS.find((key) => fees[key] !== null);
  if (known !== undefined && row.revenue_source !== "frozen") {
    return invalid("recorded_only", `${at}.${known}`, `known beside revenue_source ${show(row.revenue_source)}`);
  }
  const net = fees.organization_net ?? null;
  const minor = net === null ? null : BigInt(net.amount_minor);
  const split: [ReportFeeMember, bigint | null][] = [
    ["payout_to_organization", minor === null ? null : minor > 0n ? minor : 0n],
    ["organization_owes_voltie", minor === null ? null : minor < 0n ? -minor : 0n],
  ];
  for (const [key, expected] of split) {
    const actual = fees[key] ?? null;
    if (expected === null ? actual !== null : actual === null || BigInt(actual.amount_minor) !== expected) {
      const detail = net === null ? "known without organization_net" : `${actual === null ? "null" : actual.amount_minor} beside organization_net ${net.amount_minor}`;
      return invalid("net_split", `${at}.${key}`, detail);
    }
  }
  return { status: "value", value: fees as ReportFeesMoneyV2 };
}

function readRowMoney(row: Record<string, unknown>, path: string): FullRowMoney {
  const read = readPaymentMoneyV2(row.money_v2);
  if (read.status === "unavailable") return invalid("missing_field", `${path}.money_v2`, "money_v2");
  if (read.status === "invalid") return invalid(read.error, `${path}.money_v2${read.path ? `.${read.path}` : ""}`, read.detail);
  const known = row.outstanding_known;
  if (typeof known !== "boolean") return invalid("outstanding_known", `${path}.outstanding_known`, show(known));
  if (!known && read.value.outstanding !== null) {
    return invalid("outstanding_known", `${path}.outstanding_known`, "false beside an outstanding amount");
  }
  const fees = readRowFees(row, path, read.value);
  return fees.status === "invalid" ? fees : { status: "value", payment: read.value, fees: fees.value };
}

const ROW_DROPPED: readonly string[] = [...REPORT_ROW_LEGACY_MONEY_KEYS, "money_v2", "fees_money_v2"];
const TOTALS_DROPPED: readonly string[] = [...REPORT_TOTALS_LEGACY_MONEY_KEYS, "money_v2", "summary_money_v2"];

export function readOrgSessionsReport(body: unknown): OrgSessionsReportRead {
  if (!isRecord(body)) return invalid("not_object", "", show(body));
  if (body.money_contract !== 2) return invalid("money_contract", "money_contract", show(body.money_contract));
  const scope = body.scope;
  if (scope !== "full" && scope !== "session_only") return invalid("enum", "scope", show(scope));
  if (!Array.isArray(body.rows)) return invalid("not_array", "rows", show(body.rows));
  for (const key of ["totals", "period_totals"] as const) {
    if (!isRecord(body[key])) return invalid("not_object", key, show(body[key]));
  }

  const rows: OrgReportRow[] = [];
  for (const [i, entry] of body.rows.entries()) {
    const path = `rows[${i}]`;
    if (!isRecord(entry)) return invalid("not_object", path, show(entry));
    // The money is isolated per row: only the admin blocks are read, a manager row has none.
    const money = deepFreeze(scope === "full" ? readRowMoney(entry, path) : ({ status: "none" } as const));
    const row = frozenWithout(entry, ROW_DROPPED);
    rows.push(Object.freeze({ ...row, money }) as unknown as OrgReportRow);
  }

  // Each totals object's money is isolated like a row's: an invalid one leaves the other and the rows readable.
  const totals = (path: "totals" | "period_totals") => {
    const record = body[path] as Record<string, unknown>;
    const money = deepFreeze(scope === "full" ? readReportTotalsMoney(record, path) : ({ status: "none" } as const));
    return Object.freeze({ ...frozenWithout(record, TOTALS_DROPPED), money });
  };
  const report: Record<string, unknown> = {
    scope,
    organization: frozenCopy(body.organization),
    rows: Object.freeze(rows),
    totals: totals("totals"),
    period_totals: totals("period_totals"),
    count: body.count,
  };
  for (const key of ["range", "filters", "drivers"] as const) if (body[key] !== undefined) report[key] = frozenCopy(body[key]);
  return { status: "value", value: Object.freeze(report) as unknown as OrgReport };
}
